import React, { useState, useRef, useLayoutEffect, useEffect, useMemo } from 'react';
import {
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Divider from '@material-ui/core/Divider';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Badge from '@material-ui/core/Badge';
import Typography from '@material-ui/core/Typography';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Collapse from '@material-ui/core/Collapse';
import Avatar from '@material-ui/core/Avatar';
import { debounce } from 'lodash';

// Icons
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ViewListIcon  from '@material-ui/icons/ViewList';
import CheckIcon from '@material-ui/icons/Check';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import RefreshIcon from '@material-ui/icons/Refresh';
import GetAppIcon from '@material-ui/icons/GetApp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import FilterListIcon from '@material-ui/icons/FilterList';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import clsx from 'clsx';
import { HEADER_HEIGHT } from '../../components/MainDrawer';
import ViewColumn from '@material-ui/icons/ViewColumn';
import ViewModule from '@material-ui/icons/ViewModule';
import { RowGroupingIcon, ViewKanbanIcon } from '../common/svg';
import { OverridableComponent } from '@material-ui/core/OverridableComponent';
import { LinearProgress, SvgIconTypeMap } from '@material-ui/core';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: theme.palette.grey[50],
    },

    // Plays once on mount — every nav click swaps in a fresh PageContentLayout instance (Switch/Route
    // fully unmounts the old page), so this fires automatically on every navigation without any
    // route-level transition wiring. Same fade/rise language as AnimatedItem (components/animations/
    // AnimatedItem.tsx), just a touch slower to suit the whole page shell rather than a small item.
    '@keyframes pageContentEnter': {
      from: { opacity: 0, transform: 'translateY(8px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    pageEnter: {
      animation: '$pageContentEnter 0.18s ease',
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
      },
    },

    // ── Page header (icon + title + subtitle, mirrors HorizontalTabPage) ──
    pageHeader: {
      backgroundColor: theme.palette.background.paper,
      // inset shadow instead of borderBottom - a real border adds its own 1px on top of the
      // padding+icon height (57px total), which no longer lines up with the nav sidebar header
      // and Quick View header (both exactly 56px) - inset renders the same divider line without
      // adding to the box's height.
      boxShadow: `inset 0 -1px 0 0 ${theme.palette.divider}`,
      padding: theme.spacing(1.25, 2),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      flexShrink: 0,
      position: 'relative',
    },
    pageHeaderContent: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      flex: 1,
      overflow: 'hidden',
      // Expanding: height grows first, opacity fades in after a short delay so content
      // isn't visible popping into a still-growing box — collapsing (below) is the reverse,
      // opacity fades out immediately while the height then catches up.
      transition: 'max-height 0.28s ease, opacity 0.2s ease 0.08s',
      maxHeight: 48,
      opacity: 1,
    },
    pageHeaderContentCollapsed: {
      maxHeight: 0,
      opacity: 0,
      transition: 'max-height 0.28s ease 0.05s, opacity 0.12s ease',
    },
    // Absolutely positioned so it can never affect pageHeader's own height — shown only
    // while collapsed, in the same space the full header used to occupy.
    pageHeaderCollapsedTitle: {
      position: 'absolute',
      left: theme.spacing(2),
      top: '50%',
      transform: 'translateY(calc(-50% + 3px))',
      display: 'flex',
      alignItems: 'baseline',
      gap: 6,
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: `calc(100% - ${theme.spacing(4)}px)`,
      pointerEvents: 'none',
    },
    pageHeaderCollapsedSubtitle: {
      fontSize: 11,
      fontWeight: 400,
      color: theme.palette.text.secondary,
      opacity: 0.8,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    pageHeaderCollapseBtn: {
      position: 'absolute',
      left: '50%',
      bottom: -12,
      transform: 'translateX(-50%)',
      zIndex: 2,
      width: 24,
      height: 24,
      padding: 0,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
      },
    },
    pageHeaderIcon: {
      width: 36,
      height: 36,
      borderRadius: 8,
      backgroundColor: '#e3f2fd',
      color: theme.palette.primary.main,
      flexShrink: 0,
    },
    pageHeaderBackButton: {
      color: theme.palette.text.secondary,
      padding: 6,
      // Same small nudge-left on hover as the Enquiry form's header back link (headerBackLink in enquiryFormDrawerStyles.ts).
      '& .MuiSvgIcon-root': {
        transition: 'transform 0.15s ease',
      },
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.primary,
        '& .MuiSvgIcon-root': {
          transform: 'translateX(-2px)',
        },
      },
      '@media (prefers-reduced-motion: reduce)': {
        '&:hover .MuiSvgIcon-root': {
          transform: 'none',
        },
      },
    },
    pageHeaderBackDivider: {
      width: 1,
      height: 28,
      backgroundColor: theme.palette.divider,
      marginRight: theme.spacing(0.25),
      flexShrink: 0,
    },
    pageHeaderTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    pageHeaderSubtitle: {
      fontSize: 11,
      color: theme.palette.text.secondary,
      marginTop: 1,
    },
    pageHeaderTitleExtras: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: theme.spacing(1),
      flexShrink: 0,
    },
    pageHeaderActionButton: {
      textTransform: 'none',
      fontSize: 12,
      fontWeight: 500,
      borderRadius: 20,
      padding: '3px 12px',
      backgroundColor: 'rgba(21, 101, 192, 0.07)',
      color: theme.palette.primary.dark,
      '&:hover': {
        backgroundColor: 'rgba(21, 101, 192, 0.14)',
      },
    },

    controlPanel: {
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 2px 4px rgba(0,0,0,0.07)',
      flexShrink: 0,
    },
    // On a page that scrolls as a whole (the dashboards), the toolbar - selectors, chart options, search - stays under the app's fixed header
    // while the charts scroll under it. position: sticky needs the window to be what scrolls: see pageRootRef below.
    controlPanelSticky: {
      position: 'sticky',
      top: HEADER_HEIGHT,
      zIndex: 10,
    },

    // ── Main toolbar row ──
    toolbarRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      padding: theme.spacing(0.75, 1.5),
      minHeight: 52,
      // flexWrap is applied inline (driven by measured container width, not viewport) — see isMobile
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0.75, 1),
        gap: theme.spacing(0.25),
      },
    },

    // ── Left: search + optional inline checkboxes + filter icon ──
    leftGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.75),
      // Shrinkable (with minWidth:0) so that once toolbarRow wraps this onto its own line, it's
      // actually constrained to the real available width and its own flexWrap can kick in —
      // flexShrink:0 here would keep it at max-content width and defeat that wrap.
      flexShrink: 1,
      minWidth: 0,
      flexWrap: 'wrap' as const,
    },
    searchField: {
      width: 220,
      [theme.breakpoints.down('sm')]: { width: 160 },
      [theme.breakpoints.down('xs')]: { width: 130 },
      '& .MuiOutlinedInput-root': {
        borderRadius: 6,
        fontSize: 13,
        backgroundColor: theme.palette.grey[50],
        height: 36,
      },
      '& .MuiOutlinedInput-input': {
        padding: theme.spacing(0, 0.5),
        [theme.breakpoints.down('xs')]: { fontSize: 12 },
      },
      '& .MuiOutlinedInput-adornedStart': { paddingLeft: theme.spacing(1) },
      '& .MuiOutlinedInput-adornedEnd':   { paddingRight: theme.spacing(1) },
    },
    searchAdornIcon: {
      fontSize: 17,
      color: theme.palette.text.disabled,
    },
    clearIconBtn: {
      padding: 3,
      color: theme.palette.text.disabled,
      '&:hover': { color: theme.palette.text.secondary },
    },

    // Inline checkboxes (placement="inline")
    inlineCheckboxDivider: {
      height: 20,
      alignSelf: 'center',
      [theme.breakpoints.down('xs')]: { display: 'none' },
    },
    inlineCheckboxes: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.25),
      flexWrap: 'wrap' as const,
      [theme.breakpoints.down('xs')]: { display: 'none' },
    },
    checkboxLabel: {
      marginLeft: 0,
      marginRight: 0,
      '& .MuiFormControlLabel-label': {
        fontSize: 13,
        color: theme.palette.text.secondary,
        userSelect: 'none' as const,
        whiteSpace: 'nowrap' as const,
      },
      '& .MuiCheckbox-root': {
        padding: theme.spacing(0.4, 0.6, 0.4, 0.25),
      },
    },

    // Result count label
    resultCount: {
      fontSize: 12,
      color: theme.palette.text.disabled,
      whiteSpace: 'nowrap' as const,
      fontWeight: 400,
      userSelect: 'none' as const,
      [theme.breakpoints.down('xs')]: { display: 'none' },
    },

    // Filter icon button
    filterIconBtn: {
      height: 32,
      width: 32,
      padding: 6,
      borderRadius: 6,
      border: `1px solid ${theme.palette.grey[300]}`,
      color: theme.palette.text.secondary,
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
        borderColor: theme.palette.grey[400],
      },
    },
    filterIconBtnActive: {
      height: 32,
      width: 32,
      padding: 6,
      borderRadius: 6,
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: '#e3f2fd',
      color: theme.palette.primary.main,
      '&:hover': { backgroundColor: '#bbdefb' },
    },
    filterBadge: {
      '& .MuiBadge-dot': {
        backgroundColor: theme.palette.primary.main,
        width: 7,
        height: 7,
        minWidth: 7,
        borderRadius: '50%',
      },
    },

    // ── Advanced filter row (replaces popover) ──
    advancedFilterRow: {
      display: 'flex',
      alignItems: 'flex-start',
      flexWrap: 'wrap' as const,
      gap: theme.spacing(2),
      padding: theme.spacing(1.25, 1.5),
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.grey[50],
    },
    advancedFilterSection: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: theme.spacing(0.5),
      minWidth: 160,
      maxWidth: 500,
    },
    advancedFilterSectionTitle: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.07em',
      color: theme.palette.text.disabled,
      marginBottom: theme.spacing(0.25),
    },
    advancedFilterCheckboxes: {
      display: 'flex',
      flexDirection: 'row' as const,
      flexWrap: 'wrap' as const,
      gap: theme.spacing(0, 0.5),
    },
    advancedFilterCheckboxLabel: {
      marginLeft: 0,
      marginRight: 0,
      '& .MuiFormControlLabel-label': {
        fontSize: 13,
        color: theme.palette.text.secondary,
        userSelect: 'none' as const,
      },
      '& .MuiCheckbox-root': {
        padding: theme.spacing(0.4, 0.75, 0.4, 0.25),
      },
    },
    advancedFilterContent: {
      display: 'flex',
      alignItems: 'flex-start',
      flexWrap: 'wrap' as const,
      gap: theme.spacing(1.5),
      flex: 1,
    },
    advancedFilterDivider: {
      alignSelf: 'stretch',
      margin: theme.spacing(0, 0.5),
      [theme.breakpoints.down('xs')]: { display: 'none' },
    },
    // Always-visible checkbox row (placement="always")
    checkboxRow: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap' as const,
      gap: theme.spacing(0.25, 1.5),
      padding: theme.spacing(0.5, 1.5),
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
    },

    advancedFilterFooter: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: 'auto',
      alignSelf: 'flex-end',
      paddingBottom: theme.spacing(0.5),
    },

    // ── Center: stats or custom content — sized to its own content (not flex:1) so it stays put
    // next to the left-side items instead of being squeezed into an overlapping box when cramped.
    centerGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      flexWrap: 'wrap' as const,
    },

    // Stat cards
    statCard: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      padding: theme.spacing(0.5, 1.25),
      borderRadius: 4,
      fontWeight: 500,
      fontSize: 12,
      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    },

    // ── Right: icon buttons ──
    // flexWrap is applied inline (driven by measured container width, not viewport) — see isMobile
    rightGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.25),
      // Shrinkable (with minWidth:0) so that once it lands alone on a wrapped line, it's
      // constrained to the real available width and its own flexWrap can kick in as a last resort.
      flexShrink: 1,
      minWidth: 0,
    },
    // Applied when rightGroup wraps onto its own second row (isCramped) — matches the divider
    // treatment already used for the always-visible checkbox row.
    rightGroupWrapped: {
      borderTop: `1px solid ${theme.palette.divider}`,
      marginTop: theme.spacing(0.75),
      paddingTop: theme.spacing(0.75),
    },
    iconBtn: {
      padding: 7,
      borderRadius: 6,
      color: '#70d389',
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
      },
    },
    iconBtnDanger: {
      padding: 7,
      borderRadius: 6,
      color: theme.palette.text.disabled,
      '&:hover': {
        backgroundColor: '#fef2f2',
        color: '#c0392b',
      },
    },
    iconBtnActive: {
      padding: 7,
      borderRadius: 6,
      backgroundColor: '#e3f2fd',
      color: theme.palette.primary.main,
      '&:hover': { backgroundColor: '#bbdefb' },
    },
    toolbarDivider: {
      height: 20,
      alignSelf: 'center',
      margin: theme.spacing(0, 0.25),
    },

    toolbarExtrasBox: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flexShrink: 0,
      transition: 'opacity 0.2s ease',
      opacity: 1,
    },
    // A dashboard's selectors, responsibility pills and buttons are wider than a small screen: the box may shrink to the row's width so its
    // own content wraps, instead of running past the right edge and under the right-hand buttons.
    toolbarExtrasBoxFlexible: {
      flexShrink: 1,
      minWidth: 0,
      maxWidth: '100%',
    },

    // Saved views split button
    savedViewsGroup: {
      '& .MuiButtonGroup-groupedOutlinedHorizontal:not(:last-child)': {
        borderRightColor: theme.palette.grey[300],
      },
    },
    // Compact (icon-only, no label/arrow) treatment below is applied inline (driven by measured
    // container width, not viewport) — see isSmall
    savedViewsMainBtn: {
      fontSize: 12,
      fontWeight: 500,
      textTransform: 'none' as const,
      color: '#70d389',
      borderColor: theme.palette.grey[300],
      padding: theme.spacing(0.4, 1.25),
      height: 32,
      whiteSpace: 'nowrap' as const,
      '&:hover': {
        borderColor: theme.palette.grey[400],
        backgroundColor: theme.palette.grey[50],
      },
    },
    savedViewsMainBtnCompact: {
      padding: theme.spacing(0.4, 0.75),
      '& .MuiButton-startIcon': { margin: 0 },
    },
    savedViewsMainBtnLabel: {
      color: theme.palette.text.secondary,
    },
    savedViewsArrowBtn: {
      borderColor: theme.palette.grey[300],
      color: theme.palette.text.disabled,
      padding: theme.spacing(0.4, 0.25),
      minWidth: 26,
      height: 32,
      '&:hover': {
        borderColor: theme.palette.grey[400],
        backgroundColor: theme.palette.grey[50],
      },
    },

    menuItemIcon: { minWidth: 28 },
    menuItemSelectedLabel: { fontWeight: 600 },

    // Same neutral resting look as dlBtnPaused below (a page's own primary action, e.g.
    // EnquirySummaryPage.tsx's filled applySearchButton, is deliberately the exception) -
    // explicit request: idle and paused used to render in different colours (blue vs grey)
    // despite both meaning "nothing is actively happening right now" - the button's own label
    // ("Show all" vs "Resume") and the progress bar underneath already disambiguate the two,
    // so colour is freed up to mean exactly one thing: dlBtnDownloading's amber, "this is
    // actively happening". Consistent across every toolbar button at rest instead of each one
    // reaching for its own accent.
    dlBtnIdle: {
      fontSize: 12, fontWeight: 500, textTransform: 'none',
      height: 32, padding: theme.spacing(0.4, 1.25),
      borderColor: theme.palette.grey[300],
      color: theme.palette.text.secondary,
      '&:hover': { backgroundColor: theme.palette.grey[100] },
    },
    dlBtnDownloading: {
      fontSize: 12, fontWeight: 500, textTransform: 'none',
      height: 32, padding: theme.spacing(0.4, 1.25),
      borderColor: '#f0c060',
      color: '#854f0b',
      backgroundColor: '#faeeda',
      '&:hover': { backgroundColor: '#f5e2c0' },
    },
    dlBtnPaused: {
      fontSize: 12, fontWeight: 500, textTransform: 'none',
      height: 32, padding: theme.spacing(0.4, 1.25),
      borderColor: theme.palette.grey[300],
      color: theme.palette.text.secondary,
      '&:hover': { backgroundColor: theme.palette.grey[100] },
    },


    viewToggleGroup: {
      border: `1px solid ${theme.palette.grey[300]}`,
      borderRadius: 6,
      overflow: 'hidden',
      '& .MuiButtonGroup-groupedOutlinedHorizontal': {
        borderColor: theme.palette.grey[300],
      },
    },
    viewToggleBtn: {
      width: 34,
      height: 32,
      minWidth: 34,
      padding: 0,
      borderRadius: 0,
      color: '#70d389',
      borderColor: `${theme.palette.grey[300]} !important`,
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
      },
    },
    // "Soft" disabled look for the List/Board/Compact-board toggle buttons specifically -
    // see their own call sites' comment for why they guard the click handler instead of using
    // the real `disabled` prop (breaks ButtonGroup's own CSS otherwise).
    viewToggleBtnLoading: {
      opacity: 0.5,
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },
    viewToggleBtnActive: {
      color: '#1565c0',
      backgroundColor: '#e3f2fd',
      '&:hover': {
        backgroundColor: '#bbdefb',
      },
    },
    searchTooltipIcon: {
      fontSize: 15,
      color: theme.palette.text.disabled,
      cursor: 'help',
      '&:hover': {
        color: theme.palette.text.secondary,
      },
    },
    // Animated show/hide wrapper for icon groups in the toolbar
    iconGroupAnimate: {
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      transition: 'max-width 0.22s ease, opacity 0.18s ease',
      willChange: 'max-width, opacity',
    },

    // Shared bubble style for rich-content tooltips (searchTooltip, downloadAll.infoTooltip, etc.)
    // so every caller-supplied tooltip renders at the same text size regardless of its own markup.
    richTooltip: {
      fontSize: 12,
      lineHeight: 1.5,
      maxWidth: 320,
      '& .MuiTypography-root': {
        fontSize: 'inherit',
        lineHeight: 'inherit',
      },
    },
  })
);

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Controls where the filter checkboxes are rendered.
 *
 * - `'inline'`   — shown directly in the toolbar row next to the search input.
 *                  On mobile (xs) they automatically collapse into the advanced filter row.
 * - `'advance'`  — shown inside the collapsible advancedFilterRow below the toolbar.
 *                  If there is only one advance checkbox and no advancedFilterContent,
 *                  it renders as a single standalone checkbox (no section wrapper).
 *                  If there are multiple, they are grouped under an "Include" section label.
 * - `'always'`   — shown in a permanently-visible row directly below the toolbar.
 *                  Wraps automatically to a second line when there are many checkboxes.
 */
export type FilterCheckboxPlacement = 'inline' | 'advance' | 'always';

export interface FilterCheckbox {
  key: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** @default 'advance' */
  placement?: FilterCheckboxPlacement;
  tooltip?: string;
}

export interface StatDefinition {
  icon?: OverridableComponent<SvgIconTypeMap>;
  count: number;
  label: string;
  /** Shorter label used on mobile */
  shortLabel?: string;
  className?: string;
}

export interface ExportOption {
  label: string;
  onClick: () => void;
}

export interface SavedView {
  label: string;
  onClick: () => void;
  /** Highlights this entry in the dropdown as the currently-active view */
  isSelected?: boolean;
}

export interface ToolbarAction {
  tooltip: string;
  icon: React.ReactElement;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  /** Moves into the ⋮ overflow menu on xs instead of staying in the toolbar */
  collapseOnMobile?: boolean;
}

export interface ResultCountInfo {
  /** Number of visible/filtered rows */
  showing: number;
  /** Total rows before filter */
  total: number;
  /** Entity label e.g. "Rule Setup", "Template", "Record" */
  label: string;
  /** Optional count appended after the label e.g. { count: 22, label: 'with PMS Access' } */
  extra?: { count: number; label: string };
}

export type ViewModeType = 'list' | 'board' | 'compress_board';

export interface PageContentLayoutProps {
  // ── Page header (icon + title, like HorizontalTabPage) ──
  pageHeader?: {
    /** Icon rendered in a rounded avatar — match the sidebar nav icon for this page */
    icon?: React.ReactElement;
    title: string;
    subtitle?: string;
    onLearnMore?: () => void;
    onResourceCenter?: () => void;
    /** Rendered with the same treatment as Learn More — use for a page-specific action like "Personalise" */
    onPersonalise?: () => void;
    /** Extra custom action buttons rendered with the same treatment as Learn More, immediately after it */
    extraActions?: { label: string; icon?: React.ReactElement; onClick: () => void }[];
    /** Opt-in: renders a back arrow before the icon, mirroring FilteredVerticalTabPage's back button */
    onBack?: () => void;
    /** @default 'Back' */
    backTooltip?: string;
    /** Compact content rendered right after the title, e.g. a segmented switch between two modes of the same page */
    titleExtras?: React.ReactNode;
  };

  /**
   * Opt-in: adds a collapse/expand arrow centered on the page header's bottom border,
   * mirroring the sidebar's collapse behaviour. The collapsed/expanded state is
   * remembered in localStorage under this key, so give each page its own unique value.
   */
  collapsibleHeader?: {
    storageKey: string;
  };

  // ── Toolbar extras (renders in main toolbar row, between search area and center stats) ──
  /** Extra content rendered inline in the toolbar row — use for compact controls like a GroupBy Select */
  toolbarExtras?: React.ReactNode;
  /** Extra content at the start of the right-hand group, next to the search field and the icon buttons (e.g. a quiet status such as the last financial sync date) */
  toolbarRightExtras?: React.ReactNode;

  /**
   * Opt-out: hides the entire toolbar/control panel row (search, checkboxes, filters,
   * export/saved-views/etc.) below the page header. Use for pages that only need the
   * header + padded content body, with no grid/board controls (e.g. an admin dashboard).
   * @default false
   */
  hideToolbar?: boolean;

  // ── Search ──
  /** Opt-out: hides the search field for pages whose grid has no quick-filter search. @default false */
  hideSearch?: boolean;
  /** Where the search field renders in the toolbar row. @default 'left' */
  searchPosition?: 'left' | 'right';
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchTooltip?: React.ReactNode;
  /** Called when Enter is pressed in the search field — use for explicit-apply search patterns */
  onSearchSubmit?: (value: string) => void;
  /** An explicit-apply search button (e.g. "Apply Search") rendered right after the inline filter checkboxes, followed by a divider. Omit if search applies live. */
  searchButton?: React.ReactNode;

  // ── Filter checkboxes ──
  /**
   * Each checkbox declares its own `placement`.
   * - `'inline'`  → rendered in the toolbar row
   * - `'advance'` → rendered in the collapsible advanced filter row below the toolbar
   */
  filterCheckboxes?: FilterCheckbox[];

  // ── Advanced filter row content ──
  /**
   * Extra content (e.g. date pickers, text fields) rendered inside the
   * collapsible advancedFilterRow alongside the advance-placed checkboxes.
   */
  advancedFilterContent?: React.ReactNode;

  /** Shows active-indicator dot on the filter icon button */
  hasActiveAdvancedFilters?: boolean;

  /** Shows a small loading skeleton on the filter icon button while advancedFilterContent's own data is still fetching, without blocking the rest of the page */
  advancedFilterLoading?: boolean;

  /**
   * When provided, renders a "Clear all" button at the end of the advanced filter row.
   * No Apply button — filter state is managed externally and applied live.
   */
  onClearFilters?: () => void;

  // ── Result count ──
  resultCount?: ResultCountInfo;

  // ── Stats ──
  stats?: StatDefinition[];
  statsLoading?: boolean;
  /** Shows a skeleton placeholder at the end of the always-visible checkbox row while data loads */
  checkboxRowLoading?: boolean;
  /** Shows a skeleton placeholder in place of the main toolbar row's inline checkboxes
   * (placement="inline") while the data that determines whether/which of them to show is
   * still loading - e.g. a checkbox gated on a scope/permission query, so it doesn't just pop
   * in unannounced once that query resolves. */
  inlineCheckboxesLoading?: boolean;
  /**
   * Extra content (e.g. a clearable active-filter chip) rendered at the end of the always-
   * visible checkbox row, alongside its 'always'-placement checkboxes - a quieter, less
   * crowded line than toolbarExtras, which already carries the Key Opportunity chip/GroupBy
   * select/etc. in the main toolbar row.
   */
  checkboxRowExtras?: React.ReactNode;

  /**
   * Custom content rendered in the center toolbar area instead of the stats chips.
   * Use when the center slot needs a custom component (e.g. a StatusCard with its own query).
   */
  centerContent?: React.ReactNode;

  // ── Toolbar ──
  onAdd?: () => void;
  onEdit?: () => void;
  editDisabled?: boolean;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  onRowGrouping?: () => void;
  rowGroupingDisabled?: boolean;
  /** Highlights the row grouping button to show grouping is currently applied to the grid */
  rowGroupingActive?: boolean;
  /** Tooltip shown when row grouping is available. @default 'Row grouping' */
  rowGroupingTooltip?: string;
  /** Tooltip shown when rowGroupingDisabled is true — caller supplies the entity-specific wording (e.g. "…until all enquiries are downloaded") */
  rowGroupingDisabledTooltip?: string;
  onColumnFilter?: () => void;
  onRefresh?: () => void;
  exportOptions?: ExportOption[];
  /** A single icon action rendered immediately next to the Export button (e.g. a bulk "Data Download" alternative) */
  secondaryExportAction?: ToolbarAction;
  extraToolbarActions?: ToolbarAction[];
  /** Where extraToolbarActions render relative to Add/Edit/Delete. @default 'before' */
  extraToolbarActionsPlacement?: 'before' | 'after';

  savedView?: {
    onClick: () => void;
    savedViews?: SavedView[];
    onSaveView?: () => void;
    /** Disables the "Save current view…" menu entry. @default false */
    saveViewDisabled?: boolean;
    /** Shows a small loading skeleton in place of the icon while the saved-views list is still being fetched */
    loading?: boolean;
  };

  // ── Content ──
  children: React.ReactNode;

  downloadAll?: {
    status: 'idle' | 'downloading' | 'paused';
    onToggle: () => void;
    showing?: number;
    total?: number;
    /** Tooltip content shown on an info icon after the button — caller supplies the text */
    infoTooltip?: React.ReactNode;
  };

  viewMode?: {
    value: ViewModeType;
    onChange: (mode: ViewModeType) => void;
    /** Controls which view buttons are shown. Defaults to all modes. */
    visibleModes?: ViewModeType[];
  };

  isLoading?: boolean;

  /**
   * Opt-in: disables the internal content scrollbar (children normally scroll in their
   * own fixed-height box below the header/toolbar) and lets the browser's own page scroll
   * handle overflow instead. Use for dashboard-style pages with naturally tall, variable
   * content (e.g. a long stack of charts) rather than a grid/board meant to fit one screen.
   * @default false
   */
  pageScroll?: boolean;
}

// ─── Stat config ─────────────────────────────────────────────────────────────

// const STAT_CONFIG = {
//   pending: { icon: <ScheduleIcon style={{ fontSize: 13 }} />, cls: 'statChipPending' as const },
//   sending: { icon: <SendIcon     style={{ fontSize: 13 }} />, cls: 'statChipSending' as const },
//   sent:    { icon: <DoneAllIcon  style={{ fontSize: 13 }} />, cls: 'statChipSent'    as const },
// };

// ─── StatChips helper ─────────────────────────────────────────────────────────

const StatChips: React.FC<{
  stats: StatDefinition[];
  isMobile: boolean;
  classes: ReturnType<typeof useStyles>;
  isLoading?: boolean;
}> = ({ stats, isMobile, classes, isLoading }) => {

  if (isLoading) {
      return (
         <>
          {stats.map((_, index) => (
            <Box
              key={index}
              className={classes.statCard}
              style={{
                backgroundColor: 'rgba(0,0,0,0.04)',
                border: '1px solid rgba(0,0,0,0.06)',
                gap: 6,
              }}
            >
              <SkeletonLoader variant="shape" shape="circle" width={14} height={14} />
              <SkeletonLoader variant="shape" shape="text" width={24} height={12} />
              <SkeletonLoader variant="shape" shape="text" width={isMobile ? 32 : 80} height={12} />
            </Box>
          ))}
        </>
      )
  }

  return (
    <>
      {stats.map((stat, index) => {
        const StatIcon = stat.icon;
        return (
          <Box key={index} className={clsx(classes.statCard, stat.className)}>
            {StatIcon && <StatIcon />}
            <strong>{stat.count}</strong>
            {isMobile && stat.shortLabel ? stat.shortLabel : stat.label}
          </Box>
        );
      })}
    </>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

export const PageContentLayout: React.FC<PageContentLayoutProps> = ({
  pageHeader,
  collapsibleHeader,
  hideToolbar = false,
  toolbarExtras,
  toolbarRightExtras,
  hideSearch = false,
  searchPosition = 'left',
  search = '',
  onSearchChange = () => {},
  searchPlaceholder = 'Search…',
  onSearchSubmit,
  searchButton,
  filterCheckboxes = [],
  advancedFilterContent,
  hasActiveAdvancedFilters = false,
  advancedFilterLoading = false,
  onClearFilters,
  resultCount,
  stats = [],
  statsLoading,
  checkboxRowLoading = false,
  checkboxRowExtras,
  inlineCheckboxesLoading = false,
  centerContent,
  onAdd,
  onEdit,
  editDisabled = false,
  onDelete,
  deleteDisabled = false,
  onRowGrouping,
  rowGroupingDisabled = false,
  rowGroupingActive = false,
  rowGroupingTooltip = 'Row grouping',
  rowGroupingDisabledTooltip = 'Row grouping is unavailable until all records are downloaded',
  onColumnFilter,
  onRefresh,
  exportOptions,
  secondaryExportAction,
  savedView,
  extraToolbarActions = [],
  extraToolbarActionsPlacement = 'before',
  downloadAll,
  viewMode,
  searchTooltip,
  children,
  isLoading = false,
  pageScroll = false,
}) => {
  const classes  = useStyles();

  // Measured off the toolbar's actual rendered width rather than the viewport — a fixed
  // sidebar (or the panel being embedded/narrower than the window) means the viewport can be
  // wide while the toolbar itself is cramped, so viewport-based breakpoints alone miss that case.
  const controlPanelRef = useRef<HTMLDivElement>(null);
  const pageRootRef = useRef<HTMLDivElement>(null);

  // A sticky element sticks to the nearest ancestor that scrolls. The app's content wrappers (MainDrawer's DockedContent, #root) are
  // overflow:auto / hidden, which makes them scroll containers even though it is the window that scrolls, so the toolbar would never stick.
  // While a page that scrolls as a whole (the dashboards) is shown, those ancestors are switched to clip (clips like hidden, but is not a
  // scroll container) and put back when the page goes. Every other page is left as it is.
  useEffect(() => {
    if (!pageScroll || !pageRootRef.current) {
      return undefined;
    }
    const changedAncestors: Array<{ element: HTMLElement; overflowX: string; overflowY: string }> = [];
    let ancestor = pageRootRef.current.parentElement;
    while (ancestor && ancestor !== document.body && ancestor !== document.documentElement) {
      const computedStyle = window.getComputedStyle(ancestor);
      if (computedStyle.overflowX !== 'visible' || computedStyle.overflowY !== 'visible') {
        changedAncestors.push({ element: ancestor, overflowX: ancestor.style.overflowX, overflowY: ancestor.style.overflowY });
        ancestor.style.overflowX = 'clip';
        ancestor.style.overflowY = 'visible';
      }
      ancestor = ancestor.parentElement;
    }
    return () => {
      changedAncestors.forEach(({ element, overflowX, overflowY }) => {
        element.style.overflowX = overflowX;
        element.style.overflowY = overflowY;
      });
    };
  }, [pageScroll]);
  const [containerWidth, setContainerWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );

  useLayoutEffect(() => {
    const el = controlPanelRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width;
      if (width) {
        setContainerWidth(width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const isMobile  = containerWidth < 600;
  const isSmall   = containerWidth < 960;
  // Wider-but-still-tight container (e.g. a busy toolbar next to a fixed sidebar, or a page with
  // several stat chips / extra toolbar buttons) — folds the lower-priority icon groups into the
  // overflow menu, and drops toolbarExtras to its own line, before things start clipping.
  const isCramped = containerWidth < 1600;

  const [filterOpen,       setFilterOpen]       = useState(false);
  const [exportAnchor,     setExportAnchor]      = useState<null | HTMLElement>(null);
  const [savedViewsAnchor, setSavedViewsAnchor]  = useState<null | HTMLElement>(null);
  const [moreAnchor,       setMoreAnchor]        = useState<null | HTMLElement>(null);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(
    () => !!collapsibleHeader && localStorage.getItem(collapsibleHeader.storageKey) === 'true'
  );
  const [searchValue, setSearchValue] = useState(search);
  const onSearchChangeRef = useRef(onSearchChange);

  useEffect(() => {
    setSearchValue(search);
  }, [search]);

  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  const debouncedSearchChange = useMemo(
    () => debounce((nextValue: string) => onSearchChangeRef.current(nextValue), 500),
    []
  );

  useEffect(() => () => {
    debouncedSearchChange.cancel();
  }, [debouncedSearchChange]);

  const onToggleHeaderCollapsed = () => {
    setIsHeaderCollapsed(prev => {
      const next = !prev;
      if (collapsibleHeader) {
        localStorage.setItem(collapsibleHeader.storageKey, next ? 'true' : 'false');
      }
      return next;
    });
  };

  // Split checkboxes by placement
  const inlineCheckboxes  = filterCheckboxes.filter(cb => cb.placement === 'inline');
  // 'always' checkboxes render in a permanently-visible row — never in the collapsible row
  const alwaysCheckboxes  = filterCheckboxes.filter(cb => cb.placement === 'always');
  // advance checkboxes (and inline on mobile) go into the collapsible row
  const advanceOnlyCheckboxes = filterCheckboxes.filter(
    cb => cb.placement === 'advance' || (!cb.placement && cb.placement !== 'always')
  );
  // On mobile, inline checkboxes also collapse into the advanced row
  const advancedRowCheckboxes = isMobile
    ? filterCheckboxes.filter(cb => cb.placement === 'advance' || cb.placement === 'inline' || !cb.placement)
    : advanceOnlyCheckboxes;

  const hasAdvancedRow    = advancedRowCheckboxes.length > 0 || !!advancedFilterContent;
  const hasFilterButton   = hasAdvancedRow;

  // Whether the left group (search / checkboxes / searchButton / resultCount / downloadAll) renders
  // any visible content — the divider before toolbarExtras should only show when there's something
  // to its left to separate from, otherwise it appears as a stray leading pipe.
  const hasLeftGroupContent = (!hideSearch && searchPosition !== 'right') || inlineCheckboxes.length > 0 || inlineCheckboxesLoading
    || !!searchButton || !!resultCount || !!downloadAll;

  // Active dot: explicitly set OR any advance checkbox is checked
  const filterIsActive = hasActiveAdvancedFilters || advancedRowCheckboxes.some(cb => cb.checked);

  // Whether to show section label + grouped layout for checkboxes
  // Only group when there are multiple advance checkboxes
  const showCheckboxSection = advancedRowCheckboxes.length > 1;

  const showStats      = stats.length > 0 || !!centerContent;
  const showCrud       = onAdd || onEdit || onDelete;
  const showMiddle     = onRowGrouping || onRefresh || onColumnFilter;
  const showExport     = !!exportOptions?.length;
  const showSavedViews = !!savedView?.onClick;

  const visibleExtra  = extraToolbarActions.filter(a => !(isSmall || isMobile) || !a.collapseOnMobile);
  const overflowExtra = extraToolbarActions.filter(a =>  (isSmall || isMobile) &&  a.collapseOnMobile);

  const visibleViewModes: ViewModeType[] = viewMode?.visibleModes && viewMode.visibleModes.length > 0
    ? viewMode.visibleModes
    : ['list', 'board', 'compress_board'];

  const viewModeMeta: Record<ViewModeType, { label: string; icon: React.ReactNode }> = {
    list:           { label: 'List view',     icon: <ViewListIcon fontSize="small" /> },
    board:          { label: 'Board view',    icon: <ViewKanbanIcon fontSize="small" /> },
    compress_board: { label: 'Compact board', icon: <ViewModule fontSize="small" /> },
  };

  // Once cramped, the icon group drops to its own full-width second row (see rightGroup below) —
  // at that point it usually has plenty of room to show everything inline. Rather than folding
  // based on a guessed viewport-style breakpoint (which left a lot of unused space at ~600px
  // container widths), estimate the row's own actual unfolded width against the real measured
  // container and only fold what's needed to make it fit. CRUD and saved views never fold.
  const ICON_BTN_WIDTH  = 40; // medium IconButton, incl. its gap
  const DIVIDER_WIDTH   = 17;
  const VIEW_MODE_WIDTH = 42; // per visible mode segment in the ButtonGroup
  const SAVED_VIEWS_WIDTH = 110; // "Views" split button (label + arrow)
  const TOOLBAR_ROW_PADDING = 48; // toolbarRow's own horizontal padding + a little slack

  const crudWidth = ((onAdd ? 1 : 0) + (onEdit ? 1 : 0) + (onDelete ? 1 : 0) + visibleExtra.length) * ICON_BTN_WIDTH;
  const middleWidth = ((onRowGrouping ? 1 : 0) + (onColumnFilter ? 1 : 0) + (onRefresh ? 1 : 0)) * ICON_BTN_WIDTH;
  const viewModeWidth = viewMode ? visibleViewModes.length * VIEW_MODE_WIDTH : 0;
  const exportWidth = (showExport ? ICON_BTN_WIDTH : 0) + (secondaryExportAction ? ICON_BTN_WIDTH : 0);
  // Below isSmall, savedViews itself drops its label/arrow down to an icon-only button — see the
  // savedViewsMainBtnCompact styling below — so it needs far less width at that point.
  const savedViewsWidth = showSavedViews ? (isSmall ? ICON_BTN_WIDTH : SAVED_VIEWS_WIDTH) : 0;

  const groupCount = [showCrud || visibleExtra.length > 0, !!viewMode, showMiddle, exportWidth > 0, showSavedViews]
    .filter(Boolean).length;
  const fullRightGroupWidth = crudWidth + middleWidth + viewModeWidth + exportWidth + savedViewsWidth
    + Math.max(0, groupCount - 1) * DIVIDER_WIDTH;

  const rightGroupAvailableWidth = containerWidth - TOOLBAR_ROW_PADDING;
  const rightGroupFitsUnfolded = fullRightGroupWidth <= rightGroupAvailableWidth;

  const foldViewModeIntoOverflow = !rightGroupFitsUnfolded && !!viewMode;
  const showViewModeInline = !!viewMode && !foldViewModeIntoOverflow;
  const foldMiddleIntoOverflow = !rightGroupFitsUnfolded && showMiddle;
  const showMiddleInline = showMiddle && !foldMiddleIntoOverflow;
  const foldSecondaryExportIntoOverflow = !rightGroupFitsUnfolded && !!secondaryExportAction;
  const secondaryExportVisible = !!secondaryExportAction && !foldSecondaryExportIntoOverflow;

  const viewModeOverflowItems = (!foldViewModeIntoOverflow || !viewMode) ? [] : visibleViewModes.map(mode => ({
    icon: viewModeMeta[mode].icon,
    tooltip: viewModeMeta[mode].label,
    onClick: () => viewMode.onChange(mode),
    disabled: isLoading,
    selected: viewMode.value === mode,
  }));

  const middleOverflowItems = !foldMiddleIntoOverflow ? [] : [
    ...(onRowGrouping ? [{
      icon: <RowGroupingIcon viewBox="0 0 32 22" />,
      tooltip: rowGroupingDisabled ? rowGroupingDisabledTooltip : rowGroupingTooltip,
      onClick: onRowGrouping,
      disabled: isLoading || rowGroupingDisabled,
    }] : []),
    ...(onColumnFilter ? [{
      icon: <ViewColumn />,
      tooltip: 'Columns',
      onClick: onColumnFilter,
      disabled: isLoading,
    }] : []),
    ...(onRefresh ? [{
      icon: <RefreshIcon />,
      tooltip: 'Refresh',
      onClick: onRefresh,
      disabled: isLoading,
    }] : []),
  ];

  const secondaryExportOverflowItem = (foldSecondaryExportIntoOverflow && secondaryExportAction) ? [{
    icon: secondaryExportAction.icon,
    tooltip: secondaryExportAction.tooltip,
    onClick: secondaryExportAction.onClick,
    disabled: isLoading || secondaryExportAction.disabled,
  }] : [];

  const builtInOverflowItems = [...viewModeOverflowItems, ...middleOverflowItems, ...secondaryExportOverflowItem];
  const hasOverflow = overflowExtra.length > 0 || builtInOverflowItems.length > 0;

  const showRightGroup = !!toolbarRightExtras || showCrud || showMiddle || showExport || showSavedViews
    || visibleExtra.length > 0 || hasOverflow || !!secondaryExportAction || (!hideSearch && searchPosition === 'right');

  // Normalizes every overflow-menu icon to the same size/colour, regardless of what style the
  // consuming page (extraToolbarActions) or the built-in items originally set on it — otherwise
  // icons that were styled for an IconButton (e.g. a muted "disabled until hover" colour) look
  // inconsistent/greyed-out once dropped directly into a MenuItem.
  const renderMenuIcon = (icon: React.ReactNode) =>
    React.isValidElement(icon)
      ? React.cloneElement(icon as React.ReactElement, {
          style: { ...(icon.props as { style?: React.CSSProperties }).style, fontSize: 16, color: '#1565c0' },
        })
      : icon;

  const renderSearchField = () => (
    <TextField
      disabled={isLoading}
      className={classes.searchField}
      data-tour="dashboard-search"
      variant="outlined"
      size="small"
      placeholder={searchPlaceholder}
      value={searchValue}
      onChange={e => {
        const nextValue = e.target.value;
        setSearchValue(nextValue);
        debouncedSearchChange(nextValue);
      }}
      onKeyDown={e => {
        if (e.key === 'Enter' && onSearchSubmit) {
          debouncedSearchChange.cancel();
          onSearchChangeRef.current(searchValue);
          onSearchSubmit(searchValue);
        }
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon className={classes.searchAdornIcon} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end" style={{ gap: 2 }}>
            {searchTooltip && (
              <Tooltip
                title={searchTooltip}
                placement="bottom-start"
                interactive={true}
                classes={{ tooltip: classes.richTooltip }}
                arrow={true}
              >
                <InfoOutlinedIcon className={classes.searchTooltipIcon} />
              </Tooltip>
            )}
            {searchValue && (
              <IconButton
                size="small"
                className={classes.clearIconBtn}
                onClick={() => {
                  debouncedSearchChange.cancel();
                  setSearchValue('');
                  onSearchChange('');
                }}
                aria-label="clear search"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      height={pageScroll ? undefined : '100%'}
      minHeight={pageScroll ? '100%' : undefined}
      bgcolor="grey.50"
      className={classes.pageEnter}
    >
      {pageScroll && <div ref={pageRootRef} style={{ display: 'none' }} aria-hidden={true} />}

      {/* ══════════ PAGE HEADER (icon + title) ══════════ */}
      {pageHeader && (
        <Box className={classes.pageHeader}>
          <Box
            className={clsx(
              classes.pageHeaderContent,
              collapsibleHeader && isHeaderCollapsed && classes.pageHeaderContentCollapsed,
            )}
          >
            {pageHeader.onBack && (
              <>
                <Tooltip title={pageHeader.backTooltip ?? 'Back'} placement="bottom">
                  <IconButton
                    className={classes.pageHeaderBackButton}
                    size="small"
                    onClick={pageHeader.onBack}
                    aria-label={pageHeader.backTooltip ?? 'Back'}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <div className={classes.pageHeaderBackDivider} />
              </>
            )}
            {pageHeader.icon && (
              <Avatar className={classes.pageHeaderIcon}>
                {pageHeader.icon}
              </Avatar>
            )}
            <Box>
              <Typography className={classes.pageHeaderTitle}>{pageHeader.title}</Typography>
              {pageHeader.subtitle && (
                <Typography className={classes.pageHeaderSubtitle}>{pageHeader.subtitle}</Typography>
              )}
            </Box>
            {pageHeader.titleExtras && (
              <Box className={classes.pageHeaderTitleExtras}>
                {pageHeader.titleExtras}
              </Box>
            )}
            {(pageHeader.onLearnMore || pageHeader.onResourceCenter || pageHeader.onPersonalise || (pageHeader.extraActions && pageHeader.extraActions.length > 0)) && (
              <Box style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 6 }}>
                {pageHeader.onPersonalise && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={pageHeader.onPersonalise}
                    startIcon={<EditIcon style={{ fontSize: 14 }} />}
                    className={classes.pageHeaderActionButton}
                  >
                    Personalise
                  </Button>
                )}
                {pageHeader.onLearnMore && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={pageHeader.onLearnMore}
                    startIcon={<VideoLibraryOutlinedIcon style={{ fontSize: 14 }} />}
                    className={classes.pageHeaderActionButton}
                  >
                    Learn More
                  </Button>
                )}
                {pageHeader.extraActions && pageHeader.extraActions.map((action, index) => (
                  <Button
                    key={index}
                    variant="text"
                    size="small"
                    onClick={action.onClick}
                    startIcon={action.icon}
                    className={classes.pageHeaderActionButton}
                  >
                    {action.label}
                  </Button>
                ))}
                {pageHeader.onResourceCenter && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={pageHeader.onResourceCenter}
                    startIcon={<HelpOutlineIcon style={{ fontSize: 14 }} />}
                    className={classes.pageHeaderActionButton}
                  >
                    Resource Centre
                  </Button>
                )}
              </Box>
            )}
          </Box>

          {collapsibleHeader && isHeaderCollapsed && (
            <Typography className={classes.pageHeaderCollapsedTitle} component="div">
              <span>{pageHeader.title}</span>
              {pageHeader.subtitle && (
                <span className={classes.pageHeaderCollapsedSubtitle}>{pageHeader.subtitle}</span>
              )}
            </Typography>
          )}

          {collapsibleHeader && (
            <Tooltip title={isHeaderCollapsed ? 'Show heading' : 'Collapse heading'}>
              <IconButton
                size="small"
                className={classes.pageHeaderCollapseBtn}
                onClick={onToggleHeaderCollapsed}
                aria-label={isHeaderCollapsed ? 'Show heading' : 'Collapse heading'}
              >
                {isHeaderCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {!hideToolbar && (
      <Paper square elevation={0} className={clsx(classes.controlPanel, pageScroll && classes.controlPanelSticky)} ref={controlPanelRef}>

        {/* ══════════ MAIN TOOLBAR ROW ══════════ */}
        <Box className={classes.toolbarRow} style={{ flexWrap: isCramped || pageScroll ? 'wrap' : 'nowrap' }}>

          {/* ── LEFT: search + inline checkboxes + result count + filter btn ── */}
          <Box className={classes.leftGroup}>

            {!hideSearch && searchPosition !== 'right' && renderSearchField()}

            {/* Inline checkboxes (placement="inline", hidden on mobile via CSS) */}
            {(inlineCheckboxes.length > 0 || inlineCheckboxesLoading) && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  className={classes.inlineCheckboxDivider}
                />
                <Box className={classes.inlineCheckboxes}>
                  {inlineCheckboxesLoading ? (
                    // Two placeholders - same rect+text pairing as checkboxRowLoading below,
                    // for the same reason: this page's own two inline checkboxes gated behind
                    // one pending query (e.g. a scope/permission check), so both show a stand-in
                    // rather than popping in unannounced once it resolves.
                    <>
                      <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <SkeletonLoader variant="shape" shape="rect" width={16} height={16} style={{ borderRadius: 3, flexShrink: 0 }} />
                        <SkeletonLoader variant="shape" shape="text" width={110} height={20} />
                      </Box>
                      <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <SkeletonLoader variant="shape" shape="rect" width={16} height={16} style={{ borderRadius: 3, flexShrink: 0 }} />
                        <SkeletonLoader variant="shape" shape="text" width={110} height={20} />
                      </Box>
                    </>
                  ) : (
                    inlineCheckboxes.map(cb => (
                      <Tooltip
                        key={cb.key}
                        title={cb.tooltip || ''}
                        classes={{ tooltip: classes.richTooltip }}
                        arrow={true}
                      >
                        <FormControlLabel
                          className={classes.checkboxLabel}
                          control={
                            <Checkbox
                              size="small"
                              color="primary"
                              checked={cb.checked}
                              onChange={e => cb.onChange(e.target.checked)}
                            />
                          }
                          label={cb.label}
                        />
                      </Tooltip>
                    ))
                  )}
                </Box>
              </>
            )}

            {searchButton && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  className={classes.inlineCheckboxDivider}
                />
                {searchButton}
              </>
            )}

            {/* Result count */}
            {resultCount && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  className={classes.inlineCheckboxDivider}
                />
                <Typography className={classes.resultCount}>
                  Showing{' '}
                  <strong>{resultCount.showing}</strong>
                  {' '}of{' '}
                  <strong>{resultCount.total}</strong>
                  {' '}{resultCount.label}
                  {resultCount.extra && (
                    <>{', '}<strong>{resultCount.extra.count}</strong>{' '}{resultCount.extra.label}</>
                  )}
                </Typography>
              </>
            )}

            {downloadAll && (
                <>
                  {(showCrud || showMiddle || visibleExtra.length > 0) && (
                    <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />
                  )}

                  {/* Button + its progress bar must stay together as one unit — flexWrap:nowrap
                      here keeps them from being split across lines by leftGroup's own flexWrap.
                      The info tooltip icon now lives inside the button itself (its own endIcon),
                      matching the search field's own tooltip icon and Apply Search's own -
                      explicit request for consistency, rather than sitting outside as a
                      separate sibling element. */}
                  <Box display="flex" alignItems="center" style={{ gap: 4, flexWrap: 'nowrap', flexShrink: 0 }}>
                  <Box display="flex" flexDirection="column" alignItems="flex-end" style={{ gap: 3 }}>
                    <Tooltip title={
                      downloadAll.status === 'idle'       ? 'Show all records' :
                      downloadAll.status === 'downloading' ? 'Pause download' :
                                                            'Resume download'
                    }>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={downloadAll.onToggle}
                        startIcon={
                          downloadAll.status === 'idle'        ? <GetAppIcon style={{ fontSize: 15 }} /> :
                          downloadAll.status === 'downloading' ? <PauseIcon  style={{ fontSize: 15 }} /> :
                                                                <PlayArrowIcon style={{ fontSize: 15 }} />
                        }
                        endIcon={downloadAll.infoTooltip ? (
                          <Tooltip
                            placement="bottom-start"
                            interactive={true}
                            title={downloadAll.infoTooltip}
                            classes={{ tooltip: classes.richTooltip }}
                            arrow={true}
                          >
                            <InfoOutlinedIcon className={classes.searchTooltipIcon} />
                          </Tooltip>
                        ) : undefined}
                        className={clsx(
                            downloadAll.status === 'idle'        ? classes.dlBtnIdle :
                            downloadAll.status === 'downloading' ? classes.dlBtnDownloading :
                                                                  classes.dlBtnPaused
                        )}
                      >
                        {downloadAll.status === 'idle'        ? 'Show all' :
                        downloadAll.status === 'downloading' ? 'Pause' :
                                                                'Resume'}

                        {downloadAll.status !== 'idle' && downloadAll.showing != null && (
                          <Typography
                            component="span"
                            style={{ fontSize: 11, marginLeft: 4, opacity: 0.7 }}
                          >
                            {downloadAll.showing.toLocaleString()} / {downloadAll.total?.toLocaleString()}
                          </Typography>
                        )}
                      </Button>
                    </Tooltip>

                    {downloadAll.status !== 'idle' && downloadAll.total && (
                      <LinearProgress
                        variant="determinate"
                        value={(downloadAll.showing! / downloadAll.total) * 100}
                        style={{ width: '100%', height: 2, borderRadius: 2 }}
                        color={downloadAll.status === 'paused' ? 'secondary' : 'primary'}
                      />
                    )}
                  </Box>
                  </Box>
                </>
              )}

            {toolbarExtras && hasLeftGroupContent && (
              <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />
            )}

            {/* ── TOOLBAR EXTRAS (between search and center, e.g. GroupBy Select) — shares
                leftGroup's own flexWrap context so it packs onto the same line whenever there's
                room, instead of always forcing its own new line ── */}
            {toolbarExtras && (
              <Box className={clsx(classes.toolbarExtrasBox, pageScroll && classes.toolbarExtrasBoxFlexible)}>
                {toolbarExtras}
              </Box>
            )}

            {/* Filter toggle button — shows/hides the advanced filter row */}
            {hasFilterButton && (
              <Tooltip title={filterOpen ? 'Hide filters' : 'Show filters'}>
                <Badge
                  variant="dot"
                  color="primary"
                  invisible={!filterIsActive}
                  className={classes.filterBadge}
                  overlap="rectangular"
                >
                  <IconButton
                    size="small"
                    className={
                      filterOpen || filterIsActive
                        ? classes.filterIconBtnActive
                        : classes.filterIconBtn
                    }
                    onClick={() => setFilterOpen(prev => !prev)}
                    aria-label="toggle advanced filters"
                    aria-expanded={filterOpen}
                    data-tour="filter-toggle"
                    disabled={isLoading}
                  >
                    {advancedFilterLoading ? (
                      <SkeletonLoader variant="shape" shape="circle" width={16} height={16} />
                    ) : (
                      <FilterListIcon style={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Badge>
              </Tooltip>
            )}

          </Box>

          {/* ── CENTER: stats or custom content — centered across the full toolbar when there's
              room (flex:1); once cramped it drops the forced centering and just flows next to the
              left-side items instead of being squeezed into an overlapping box ── */}
          {showStats && (
            <Box
              className={classes.centerGroup}
              style={!isCramped ? { flex: 1, justifyContent: 'center' } : undefined}
            >
              {centerContent ?? <StatChips stats={stats} isMobile={isMobile} classes={classes} isLoading={statsLoading ?? isLoading} />}
            </Box>
          )}

          {/* ── RIGHT: icon buttons — marginLeft:auto pushes it to the far right when there's
              room; pushed to its own row (flexBasis:100%) once cramped, so it never shares a
              line with (or overlaps) the left-side content ── */}
          {showRightGroup && (
            <Box
              className={clsx(classes.rightGroup, isCramped && classes.rightGroupWrapped)}
              style={{
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                marginLeft: 'auto',
                ...(isCramped ? { flexBasis: '100%', maxWidth: '100%', justifyContent: 'flex-end' } : {}),
              }}
            >

              {toolbarRightExtras}

              {!hideSearch && searchPosition === 'right' && (
                <>
                  {renderSearchField()}
                  {(visibleExtra.length > 0 || showCrud || showMiddleInline || showExport || showSavedViews || showViewModeInline || hasOverflow) && (
                    <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />
                  )}
                </>
              )}

              {/* Add / Edit / Delete and extraToolbarActions (e.g. AdHoc, Resend, Reset) — order
                  controlled by extraToolbarActionsPlacement so callers where the extras ARE the
                  add action (e.g. TasksSummaryPage's "Add AdHoc Task"/"Add Phone Message") can
                  keep them first, while callers with a real onAdd can put CRUD first instead. The
                  divider always sits between the two blocks, whichever renders first. */}
              {(() => {
                const crudButtons = (
                  <React.Fragment>
                    {onAdd && (
                      <Tooltip title="Add">
                        <span>
                          <IconButton size="medium" className={classes.iconBtn} onClick={onAdd} disabled={isLoading}>
                            <AddIcon fontSize="medium" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {onEdit && (
                      <Tooltip title="Edit">
                        <span>
                          <IconButton size="medium" className={classes.iconBtn} disabled={editDisabled || isLoading} onClick={onEdit}>
                            <EditIcon fontSize="medium" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {onDelete && (
                      <Tooltip title="Delete">
                        <span>
                          <IconButton size="medium" className={classes.iconBtn} disabled={deleteDisabled || isLoading} onClick={onDelete}>
                            <DeleteIcon fontSize="medium" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </React.Fragment>
                );

                const extraButtons = (
                  <React.Fragment>
                    {visibleExtra.map((action, i) => (
                      <Tooltip key={i} title={action.tooltip}>
                        <span>
                          <IconButton
                            size="medium"
                            className={action.danger ? classes.iconBtnDanger : classes.iconBtn}
                            disabled={action.disabled || isLoading}
                            onClick={action.onClick}
                          >
                            {action.icon}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ))}
                  </React.Fragment>
                );

                const divider = showCrud && visibleExtra.length > 0 && (
                  <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />
                );

                return extraToolbarActionsPlacement === 'after' ? (
                  <React.Fragment>
                    {crudButtons}
                    {divider}
                    {extraButtons}
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {extraButtons}
                    {divider}
                    {crudButtons}
                  </React.Fragment>
                );
              })()}

              {(showCrud || visibleExtra.length > 0) && showViewModeInline && (
                <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />
              )}

              {showViewModeInline && viewMode && (
                <ButtonGroup variant="outlined" size="small" className={classes.viewToggleGroup}>
                  {/* Explicit bug report, round 2: wrapping each Button below (span, or the
                      ButtonGroupTooltipAnchor this replaces) broke this ButtonGroup's own visual
                      grouping - its border/margin-collapsing CSS (groupedOutlinedHorizontal,
                      this file's own viewToggleGroup rule) uses :not(:first-child)/
                      :not(:last-child) selectors that only work when each Button is a genuine
                      DIRECT flex-child of ButtonGroup's root; any wrapper nests it one level
                      deeper and breaks that adjacency regardless of which element the class
                      lands on. Fixed instead by never disabling these Buttons for real - the
                      onClick guard below blocks the action while isLoading, and
                      viewToggleBtnLoading gives the same visual/cursor feedback a disabled
                      button would - so Tooltip's direct child stays the real Button (no wrapper
                      needed, and no "disabled child" warning either, since it's never actually
                      disabled). */}
                  {visibleViewModes.includes('list') && (
                    <Tooltip title="List view">
                      <Button
                        className={clsx(
                          classes.viewToggleBtn,
                          viewMode.value === 'list' && classes.viewToggleBtnActive,
                          isLoading && classes.viewToggleBtnLoading,
                        )}
                        onClick={() => { if (!isLoading) { viewMode.onChange('list'); } }}
                        aria-pressed={viewMode.value === 'list'}
                        size="medium"
                      >
                        <ViewListIcon fontSize="medium" />
                      </Button>
                    </Tooltip>
                  )}
                  {visibleViewModes.includes('board') && (
                    <Tooltip title="Board view">
                      <Button
                        className={clsx(
                          classes.viewToggleBtn,
                          viewMode.value === 'board' && classes.viewToggleBtnActive,
                          isLoading && classes.viewToggleBtnLoading,
                        )}
                        onClick={() => { if (!isLoading) { viewMode.onChange('board'); } }}
                        aria-pressed={viewMode.value === 'board'}
                        size="medium"
                      >
                        <ViewKanbanIcon fontSize="medium" />
                      </Button>
                    </Tooltip>
                  )}
                  {visibleViewModes.includes('compress_board') && (
                    <Tooltip title="Compact board">
                      <Button
                        className={clsx(
                          classes.viewToggleBtn,
                          viewMode.value === 'compress_board' && classes.viewToggleBtnActive,
                          isLoading && classes.viewToggleBtnLoading,
                        )}
                        onClick={() => { if (!isLoading) { viewMode.onChange('compress_board'); } }}
                        aria-pressed={viewMode.value === 'compress_board'}
                        size="medium"
                      >
                        <ViewModule fontSize="medium" />
                      </Button>
                    </Tooltip>
                  )}
                </ButtonGroup>
              )}

              {(showCrud || visibleExtra.length > 0 || showViewModeInline) && showMiddleInline && (
                <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />
              )}

              {/* Middle section (row grouping, columns, refresh) — animates in/out; folds into the
                  overflow menu instead (see foldMiddleIntoOverflow) once the toolbar is cramped */}
              <Box
                className={classes.iconGroupAnimate}
                style={{
                  maxWidth: showMiddleInline ? 160 : 0,
                  opacity: showMiddleInline ? 1 : 0,
                  pointerEvents: showMiddleInline ? 'auto' : 'none',
                }}
              >
                {onRowGrouping && (
                  <Tooltip title={rowGroupingDisabled ? rowGroupingDisabledTooltip : rowGroupingTooltip}>
                    <span>
                      <IconButton
                        size="medium"
                        className={rowGroupingActive ? classes.iconBtnActive : classes.iconBtn}
                        onClick={onRowGrouping}
                        disabled={isLoading || rowGroupingDisabled}
                      >
                        <RowGroupingIcon viewBox="0 0 32 22"/>
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {onColumnFilter && (
                  <Tooltip title="Columns">
                    <span>
                      <IconButton size="medium" className={classes.iconBtn} onClick={onColumnFilter} disabled={isLoading}>
                        <ViewColumn fontSize="medium" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {onRefresh && (
                  <Tooltip title="Refresh">
                    <span>
                      <IconButton size="medium" className={classes.iconBtn} onClick={onRefresh} disabled={isLoading}>
                        <RefreshIcon fontSize="medium" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>

              {(showCrud || visibleExtra.length > 0 || showMiddleInline || showViewModeInline) && (showExport || showSavedViews || secondaryExportVisible) && (
                <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />
              )}

              {/* Secondary export action — rendered directly next to Export, e.g. a bulk "Data Download" alternative;
                  folds into the overflow menu instead once the toolbar is cramped */}
              {secondaryExportVisible && (
                <Tooltip title={secondaryExportAction.tooltip}>
                  <span>
                    <IconButton
                      size="medium"
                      className={classes.iconBtn}
                      onClick={secondaryExportAction.onClick}
                      disabled={isLoading || secondaryExportAction.disabled}
                    >
                      {secondaryExportAction.icon}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* Export — icon animates in/out; Menu is a portal so stays outside the clip box */}
              <Box
                className={classes.iconGroupAnimate}
                style={{
                  maxWidth: showExport ? 50 : 0,
                  opacity: showExport ? 1 : 0,
                  pointerEvents: showExport ? 'auto' : 'none',
                }}
              >
                <Tooltip title="Export">
                  <span>
                    <IconButton
                      size="medium"
                      className={classes.iconBtn}
                      onClick={e => setExportAnchor(e.currentTarget)}
                      disabled={isLoading}
                    >
                      <GetAppIcon fontSize="medium" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              {showExport && (
                <Menu
                  anchorEl={exportAnchor}
                  open={Boolean(exportAnchor)}
                  onClose={() => setExportAnchor(null)}
                  getContentAnchorEl={null}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  {exportOptions!.map((opt, i) => (
                    <MenuItem key={i} dense onClick={() => { opt.onClick(); setExportAnchor(null); }}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Menu>
              )}

              {(showExport || secondaryExportVisible) && showSavedViews && (
                <Divider orientation="vertical" flexItem className={classes.toolbarDivider} />
              )}

              {/* Saved views split button */}
              {showSavedViews && (
                <>
                  {savedView?.loading ? (
                    <SkeletonLoader variant="shape" shape="rect" width={70} height={30} style={{ borderRadius: 6 }} />
                  ) : (
                    <ButtonGroup variant="outlined" size="small" className={classes.savedViewsGroup}>
                      <Button
                        className={clsx(classes.savedViewsMainBtn, isSmall && classes.savedViewsMainBtnCompact)}
                        startIcon={<VisibilityIcon style={{ fontSize: 15 }} />}
                        onClick={isSmall ? e => setSavedViewsAnchor(e.currentTarget) : savedView!.onClick}
                        disabled={isLoading}
                      >
                        {!isSmall && <span className={classes.savedViewsMainBtnLabel}>Views</span>}
                      </Button>
                      {!isSmall && (
                        <Button
                          className={classes.savedViewsArrowBtn}
                          onClick={e => setSavedViewsAnchor(e.currentTarget)}
                          aria-label="open saved views menu"
                          disabled={isLoading}
                        >
                          <ArrowDropDownIcon fontSize="small" />
                        </Button>
                      )}
                    </ButtonGroup>
                  )}

                  <Menu
                    anchorEl={savedViewsAnchor}
                    open={Boolean(savedViewsAnchor)}
                    onClose={() => setSavedViewsAnchor(null)}
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    {savedView?.savedViews!.map((view, i) => (
                      <MenuItem
                        key={i}
                        dense
                        selected={view.isSelected}
                        onClick={() => { view.onClick(); setSavedViewsAnchor(null); }}
                      >
                        <ListItemIcon className={classes.menuItemIcon}>
                          {renderMenuIcon(view.isSelected ? <CheckIcon /> : <ViewListIcon />)}
                        </ListItemIcon>
                        <span className={view.isSelected ? classes.menuItemSelectedLabel : undefined}>{view.label}</span>
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem dense disabled={savedView?.saveViewDisabled} onClick={() => { savedView?.onSaveView?.(); setSavedViewsAnchor(null); }}>
                      Save current view…
                    </MenuItem>
                  </Menu>
                </>
              )}

              {/* Overflow menu — collects extraToolbarActions marked collapseOnMobile plus, once the
                  toolbar is cramped, the folded middle tools / secondary export action */}
              {hasOverflow && (
                <>
                  <Tooltip title="More">
                    <span>
                      <IconButton
                        size="small"
                        className={classes.iconBtn}
                        onClick={e => setMoreAnchor(e.currentTarget)}
                        disabled={isLoading}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Menu
                    anchorEl={moreAnchor}
                    open={Boolean(moreAnchor)}
                    onClose={() => setMoreAnchor(null)}
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    {builtInOverflowItems.map((action, i) => (
                      <MenuItem
                        key={`built-in-${i}`}
                        dense
                        selected={'selected' in action ? action.selected : false}
                        disabled={action.disabled}
                        onClick={() => { action.onClick(); setMoreAnchor(null); }}
                      >
                        <ListItemIcon className={classes.menuItemIcon}>
                          {renderMenuIcon(action.icon)}
                        </ListItemIcon>
                        {action.tooltip}
                      </MenuItem>
                    ))}
                    {builtInOverflowItems.length > 0 && overflowExtra.length > 0 && <Divider />}
                    {overflowExtra.map((action, i) => (
                      <MenuItem
                        key={`extra-${i}`}
                        dense
                        disabled={action.disabled}
                        onClick={() => { action.onClick(); setMoreAnchor(null); }}
                      >
                        <ListItemIcon className={classes.menuItemIcon}>
                          {renderMenuIcon(action.icon)}
                        </ListItemIcon>
                        {action.tooltip}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}

            </Box>
          )}
        </Box>

        {/* ══════════ ALWAYS-VISIBLE CHECKBOX ROW ══════════ */}
        {(alwaysCheckboxes.length > 0 || checkboxRowLoading || checkboxRowExtras) && (
          <Box className={classes.checkboxRow}>
            {alwaysCheckboxes.map(cb => (
              <Tooltip
                key={cb.key}
                title={cb.tooltip || ''}
                classes={{ tooltip: classes.richTooltip }}
                arrow={true}
              >
                <FormControlLabel
                  key={cb.key}
                  className={classes.checkboxLabel}
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={cb.checked}
                      onChange={e => cb.onChange(e.target.checked)}
                      disabled={isLoading}
                    />
                  }
                  label={cb.label}
                />
              </Tooltip>
            ))}
            {checkboxRowLoading && (
              <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 4 }}>
                <SkeletonLoader variant="shape" shape="rect" width={16} height={16} style={{ borderRadius: 3, flexShrink: 0 }} />
                <SkeletonLoader variant="shape" shape="text" width={130} height={20} />
              </Box>
            )}
            {checkboxRowExtras}
          </Box>
        )}

        {/* ══════════ ADVANCED FILTER ROW (collapsible) ══════════ */}
        {hasAdvancedRow && (
          <Collapse in={filterOpen}>
            <Box className={classes.advancedFilterRow}>

              {/* Checkboxes section */}
              {advancedRowCheckboxes.length > 0 && (
                showCheckboxSection ? (
                  // Multiple → group under "Include" label
                  <Box className={classes.advancedFilterSection}>
                    {/* <Typography className={classes.advancedFilterSectionTitle}>
                      Include
                    </Typography> */}
                    <Box className={classes.advancedFilterCheckboxes}>
                      {advancedRowCheckboxes.map(cb => (
                        <Tooltip
                          key={cb.key}
                          title={cb.tooltip || ''}
                          classes={{ tooltip: classes.richTooltip }}
                          arrow={true}
                        >
                          <span>
                            <FormControlLabel
                              className={classes.advancedFilterCheckboxLabel}
                              control={
                                <Checkbox
                                  size="small"
                                  color="primary"
                                  checked={cb.checked}
                                  onChange={e => cb.onChange(e.target.checked)}
                                />
                              }
                              label={cb.label}
                              disabled={isLoading}
                            />
                          </span>
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  // Single → render as standalone checkbox, no wrapper
                  <FormControlLabel
                    key={advancedRowCheckboxes[0].key}
                    className={classes.advancedFilterCheckboxLabel}
                    control={
                      <Checkbox
                        size="small"
                        color="primary"
                        checked={advancedRowCheckboxes[0].checked}
                        onChange={e => advancedRowCheckboxes[0].onChange(e.target.checked)}
                      />
                    }
                    label={advancedRowCheckboxes[0].label}
                    disabled={isLoading}
                  />
                )
              )}

              {/* Vertical divider between checkbox section and extra content */}
              {advancedRowCheckboxes.length > 0 && advancedFilterContent && (
                <Divider
                  orientation="vertical"
                  flexItem
                  className={classes.advancedFilterDivider}
                />
              )}

              {/* Custom filter fields (date pickers, selects, etc.) */}
              {advancedFilterContent && (
                <Box className={classes.advancedFilterContent}>
                  {advancedFilterContent}
                </Box>
              )}

              {/* Clear all — pinned to the right, only when callback provided */}
              {onClearFilters && (
                <Box className={classes.advancedFilterFooter}>
                  <Button
                    // size="small"
                    onClick={onClearFilters}
                    disabled={isLoading}
                    // style={{ textTransform: 'none', fontSize: 12 }}
                    variant="outlined"
                    type="button"
                  >
                    Clear All
                  </Button>
                </Box>
              )}

            </Box>
          </Collapse>
        )}

      </Paper>
      )}

      {/* ══════════ CONTENT ══════════ */}
      <Box
        flex={1}
        overflow={pageScroll ? 'visible' : 'auto'}
        minHeight={pageScroll ? undefined : 0}
        display="flex"
        flexDirection="column"
        padding="20px 20px"
      >
        {children}
      </Box>

    </Box>
  );
};